import { Graphics } from 'pixi.js';
import {
  TILE_SIZE, OFFICE_COLS, OFFICE_ROWS, SCALE_FACTOR,
  DANCE_FLOOR_X, DANCE_FLOOR_Y, DANCE_FLOOR_W, DANCE_FLOOR_H,
  SLEEP_AREA_X, SLEEP_AREA_Y, SLEEPING_SPOTS,
} from '../constants/officeLayout';

const FLOOR_DARK = 0x1E2235;
const FLOOR_LIGHT = 0x232840;
const WALL_COLOR = 0x363B56;
const WALL_BORDER = 0x2D3148;
const BASEBOARD = 0x1A1D2E;

export function drawOfficeFloor(g: Graphics) {
  // Draw wall (top 2 rows)
  for (let col = 0; col < OFFICE_COLS; col++) {
    g.rect(col * TILE_SIZE, 0, TILE_SIZE, TILE_SIZE * 2);
    g.fill(col % 2 === 0 ? WALL_COLOR : WALL_BORDER);
  }

  // Baseboard line
  g.rect(0, TILE_SIZE * 2 - 2, OFFICE_COLS * TILE_SIZE, 4);
  g.fill(BASEBOARD);

  // Draw floor (checkerboard carpet)
  for (let row = 2; row < OFFICE_ROWS; row++) {
    for (let col = 0; col < OFFICE_COLS; col++) {
      const isDark = (row + col) % 2 === 0;
      g.rect(col * TILE_SIZE, row * TILE_SIZE, TILE_SIZE, TILE_SIZE);
      g.fill(isDark ? FLOOR_DARK : FLOOR_LIGHT);

      if (isDark) {
        g.rect(col * TILE_SIZE + 6, row * TILE_SIZE + 6, 2 * SCALE_FACTOR, 2 * SCALE_FACTOR);
        g.fill(0x1A1D2E);
        g.rect(col * TILE_SIZE + 30, row * TILE_SIZE + 30, 2 * SCALE_FACTOR, 2 * SCALE_FACTOR);
        g.fill(0x1A1D2E);
      }
    }
  }

  // Wall banner frame (content is animated in PixiOffice)
  const bannerX = TILE_SIZE * 3;
  const bannerY = TILE_SIZE * 0.2;
  const bannerW = TILE_SIZE * 12;
  const bannerH = TILE_SIZE * 1.3;
  g.roundRect(bannerX, bannerY, bannerW, bannerH, 4);
  g.fill(0x0f1118);
  g.stroke({ width: 2, color: 0x7c6cf7 });
  // Inner glow border
  g.roundRect(bannerX + 3, bannerY + 3, bannerW - 6, bannerH - 6, 3);
  g.stroke({ width: 1, color: 0x4c3fb8 });

  // Whiteboard frame (chart is animated in PixiOffice)
  const wbX = TILE_SIZE * 16;
  const wbY = TILE_SIZE * 0.15;
  const wbW = TILE_SIZE * 3.5;
  const wbH = TILE_SIZE * 1.6;
  g.roundRect(wbX, wbY, wbW, wbH, 2);
  g.fill(0x0f1118);
  g.stroke({ width: 2, color: 0x94A3B8 });
  // Whiteboard inner area
  g.roundRect(wbX + 4, wbY + 4, wbW - 8, wbH - 8, 1);
  g.fill(0x1a1d2e);

  // Potted plants
  const plantPositions = [
    { x: TILE_SIZE * 0.3, y: TILE_SIZE * 2.2 },
    { x: TILE_SIZE * 19.2, y: TILE_SIZE * 2.2 },
    { x: TILE_SIZE * 0.3, y: TILE_SIZE * 13.2 },
    { x: TILE_SIZE * 19.2, y: TILE_SIZE * 13.2 },
  ];

  for (const pos of plantPositions) {
    g.roundRect(pos.x, pos.y + 18, 24, 20, 2);
    g.fill(0xB45309);
    g.stroke({ width: 1, color: 0x92400E });
    g.circle(pos.x + 12, pos.y + 10, 12);
    g.fill(0x16A34A);
    g.circle(pos.x + 6, pos.y + 6, 8);
    g.fill(0x22C55E);
    g.circle(pos.x + 18, pos.y + 8, 8);
    g.fill(0x22C55E);
  }

  // Water cooler
  const wcX = TILE_SIZE * 18.5;
  const wcY = TILE_SIZE * 6;
  g.roundRect(wcX + 4, wcY, 16, 24, 4);
  g.fill(0xBFDBFE);
  g.stroke({ width: 1, color: 0x60A5FA });
  g.roundRect(wcX, wcY + 24, 24, 28, 2);
  g.fill(0xE5E7EB);
  g.stroke({ width: 1, color: 0x9CA3AF });
  g.rect(wcX + 2, wcY + 52, 4, 8);
  g.fill(0x6B7280);
  g.rect(wcX + 18, wcY + 52, 4, 8);
  g.fill(0x6B7280);

  // Printer / Copy machine (next to water cooler, right side)
  const prX = TILE_SIZE * 18.2;
  const prY = TILE_SIZE * 8;
  // Main body
  g.roundRect(prX, prY, 48, 36, 3);
  g.fill(0x374151);
  g.stroke({ width: 1, color: 0x1F2937 });
  // Paper tray top
  g.roundRect(prX + 4, prY - 6, 40, 10, 2);
  g.fill(0x4B5563);
  g.stroke({ width: 1, color: 0x374151 });
  // Paper sheets sticking out
  g.rect(prX + 10, prY - 10, 28, 6);
  g.fill(0xF1F5F9);
  g.stroke({ width: 0.5, color: 0xE5E7EB });
  // Screen/display
  g.roundRect(prX + 8, prY + 6, 16, 10, 1);
  g.fill(0x22C55E);
  g.stroke({ width: 1, color: 0x1F2937 });
  // Output tray
  g.roundRect(prX + 4, prY + 28, 40, 8, 2);
  g.fill(0x4B5563);
  // Legs
  g.rect(prX + 4, prY + 36, 4, 10);
  g.fill(0x1F2937);
  g.rect(prX + 40, prY + 36, 4, 10);
  g.fill(0x1F2937);

  // Coffee stand (next to upper-right potted plant) — wider counter
  const cfX = TILE_SIZE * 18.2;
  const cfY = TILE_SIZE * 2.3;
  // Counter table (wider)
  g.roundRect(cfX - 8, cfY + 16, 52, 24, 2);
  g.fill(0x5C4033);
  g.stroke({ width: 1, color: 0x3E2723 });
  // Table legs
  g.rect(cfX - 6, cfY + 40, 3, 10);
  g.fill(0x3E2723);
  g.rect(cfX + 39, cfY + 40, 3, 10);
  g.fill(0x3E2723);
  // Coffee machine body
  g.roundRect(cfX - 2, cfY, 20, 18, 2);
  g.fill(0x374151);
  g.stroke({ width: 1, color: 0x1F2937 });
  // Red power light
  g.circle(cfX + 4, cfY + 12, 2);
  g.fill(0xEF4444);
  // Coffee pot (carafe)
  g.roundRect(cfX + 22, cfY + 4, 12, 14, 2);
  g.fill(0x1F2937);
  g.stroke({ width: 1, color: 0x111827 });
  // Coffee inside pot
  g.roundRect(cfX + 23, cfY + 8, 10, 8, 1);
  g.fill(0x78350F);
  // Stack of cups next to carafe
  g.roundRect(cfX + 36, cfY + 8, 6, 10, 1);
  g.fill(0xE5E7EB);
  g.stroke({ width: 0.5, color: 0x9CA3AF });
  // Steam lines (from pot)
  g.moveTo(cfX + 26, cfY);
  g.lineTo(cfX + 28, cfY - 6);
  g.stroke({ width: 1, color: 0x9CA3AF });
  g.moveTo(cfX + 30, cfY + 1);
  g.lineTo(cfX + 31, cfY - 4);
  g.stroke({ width: 1, color: 0x9CA3AF });

  // ===== LOUNGE AREA (rows 13-19) =====


  // Slightly different floor tint for lounge
  for (let row = 13; row < OFFICE_ROWS; row++) {
    for (let col = 0; col < OFFICE_COLS; col++) {
      const isDark = (row + col) % 2 === 0;
      g.rect(col * TILE_SIZE, row * TILE_SIZE, TILE_SIZE, TILE_SIZE);
      g.fill(isDark ? 0x1B1F30 : 0x202538);
    }
  }

  // --- Sleeping Area (bottom-left) ---
  // Area border
  g.roundRect(SLEEP_AREA_X - 4, SLEEP_AREA_Y - 4, TILE_SIZE * 5.5 + 8, TILE_SIZE * 5 + 8, 6);
  g.stroke({ width: 1.5, color: 0x4c3fb8, alpha: 0.4 });
  // Sleeping mats on the floor for each spot
  const matColors = [0x1E3A5F, 0x3B1E5F, 0x1E5F3B, 0x5F3B1E, 0x1E5F5F, 0x5F1E3B];
  for (let si = 0; si < SLEEPING_SPOTS.length; si++) {
    const spot = SLEEPING_SPOTS[si];
    // Mat/blanket on floor
    g.roundRect(spot.x - 4, spot.y + 10, TILE_SIZE * 2, TILE_SIZE * 1.2, 4);
    g.fill({ color: matColors[si % matColors.length], alpha: 0.5 });
    g.stroke({ width: 1, color: 0x374151, alpha: 0.4 });
    // Pillow
    g.roundRect(spot.x + TILE_SIZE * 1.4, spot.y + 14, 18, 12, 4);
    g.fill({ color: 0xE2E8F0, alpha: 0.4 });
  }
  // "ZZZ" label
  g.roundRect(SLEEP_AREA_X + TILE_SIZE * 1.5, SLEEP_AREA_Y - 2, 48, 14, 3);
  g.fill(0x1A1D2E);
  g.stroke({ width: 1, color: 0x4c3fb8 });

  // --- Static dance floor tiles (base pattern) ---
  const dfTileS = 24;
  const dfCols = Math.floor(DANCE_FLOOR_W / dfTileS);
  const dfRows = Math.floor(DANCE_FLOOR_H / dfTileS);
  const dfColors = [0x2D1B69, 0x1B2D69, 0x691B2D, 0x1B6945, 0x69451B];
  for (let r = 0; r < dfRows; r++) {
    for (let c = 0; c < dfCols; c++) {
      const ci = (r * dfCols + c) % dfColors.length;
      g.rect(DANCE_FLOOR_X + c * dfTileS, DANCE_FLOOR_Y + r * dfTileS, dfTileS - 1, dfTileS - 1);
      g.fill(dfColors[ci]);
    }
  }
  // Dance floor border
  g.roundRect(DANCE_FLOOR_X - 2, DANCE_FLOOR_Y - 2, DANCE_FLOOR_W + 4, DANCE_FLOOR_H + 4, 2);
  g.stroke({ width: 2, color: 0x7C6CF7 });
}
